import { fontAttrs } from "@/components/tools/printables/page-kit";
import type {
  Box,
  TicketLayout,
  TicketLine,
} from "@/lib/tool-kit/printables/exit-ticket";
import { r2 } from "@/lib/tool-kit/printables/page";

const INK = "#1a1a1a";
const MUTED = "#555555";
const RULE = "#8a8a8a";
const SANS = { kind: "sans" } as const;
const BOLD = { kind: "sans", bold: true } as const;

function Text({
  line,
  bold = false,
  fill = INK,
  anchor = "start",
}: {
  line: TicketLine;
  bold?: boolean;
  fill?: string;
  anchor?: "start" | "middle";
}) {
  return (
    <text
      fill={fill}
      fontSize={line.size}
      textAnchor={anchor}
      x={line.x}
      y={line.y}
      {...fontAttrs(bold ? BOLD : SANS)}
    >
      {line.text}
    </text>
  );
}

function Field({
  field,
}: {
  field: { label: TicketLine; x1: number; x2: number; y: number };
}) {
  return (
    <g>
      <Text fill={MUTED} line={field.label} />
      <line
        stroke={INK}
        strokeWidth={0.25}
        x1={field.x1}
        x2={field.x2}
        y1={field.y}
        y2={field.y}
      />
    </g>
  );
}

function Rating({
  rating,
  traffic,
}: {
  rating: NonNullable<TicketLayout["rating"]>;
  traffic: boolean;
}) {
  return (
    <g>
      {rating.prompt.map((line) => (
        <Text bold key={line.y} line={line} />
      ))}
      {rating.lowLabel ? <Text fill={MUTED} line={rating.lowLabel} /> : null}
      {rating.highLabel ? <Text fill={MUTED} line={rating.highLabel} /> : null}
      {rating.marks.map((mark) => (
        <g key={mark.cx}>
          <circle
            cx={mark.cx}
            cy={mark.cy}
            fill="none"
            r={rating.radius}
            stroke={INK}
            strokeWidth={0.35}
          />
          {traffic ? (
            <text
              fill={INK}
              fontSize={r2(rating.radius * 0.95)}
              x={r2(mark.cx + rating.radius + 1.8)}
              y={r2(mark.cy + rating.radius * 0.33)}
              {...fontAttrs(SANS)}
            >
              {mark.label}
            </text>
          ) : (
            <text
              fill={INK}
              fontSize={r2(rating.radius * 1.1)}
              textAnchor="middle"
              x={mark.cx}
              y={r2(mark.cy + rating.radius * 0.38)}
              {...fontAttrs(SANS)}
            >
              {mark.label}
            </text>
          )}
        </g>
      ))}
    </g>
  );
}

/** One ticket, drawn at its own origin; the caller translates it into place. */
export function TicketArt({
  layout,
  traffic,
}: {
  layout: TicketLayout;
  traffic: boolean;
}) {
  const { inner } = layout;
  return (
    <g>
      {layout.title ? <Text bold line={layout.title} /> : null}
      {layout.nameLine ? <Field field={layout.nameLine} /> : null}
      {layout.dateLine ? <Field field={layout.dateLine} /> : null}
      {layout.topic ? <Field field={layout.topic} /> : null}
      {layout.prompts.map((prompt) => (
        <g key={prompt.text[0]?.y}>
          {prompt.text.map((line) => (
            <Text bold key={line.y} line={line} />
          ))}
          {prompt.answerLines.map((y) => (
            <line
              key={y}
              stroke={RULE}
              strokeWidth={0.25}
              x1={r2(inner.x)}
              x2={r2(inner.x + inner.width)}
              y1={y}
              y2={y}
            />
          ))}
        </g>
      ))}
      {layout.rating ? (
        <Rating rating={layout.rating} traffic={traffic} />
      ) : null}
    </g>
  );
}

/** The sheet: a ticket in every cell and dashed cut lines between them. */
export function TicketSheet({
  cells,
  layout,
  cuts,
  traffic,
}: {
  cells: readonly Box[];
  layout: TicketLayout;
  cuts: readonly { x1: number; y1: number; x2: number; y2: number }[];
  traffic: boolean;
}) {
  return (
    <g>
      {cells.map((cell) => (
        <g
          key={`${cell.x}-${cell.y}`}
          transform={`translate(${cell.x} ${cell.y})`}
        >
          <TicketArt layout={layout} traffic={traffic} />
        </g>
      ))}
      {cuts.map((cut) => (
        <line
          key={`${cut.x1}-${cut.y1}-${cut.x2}-${cut.y2}`}
          stroke="#9a9a9a"
          strokeDasharray="2 1.5"
          strokeWidth={0.25}
          x1={cut.x1}
          x2={cut.x2}
          y1={cut.y1}
          y2={cut.y2}
        />
      ))}
    </g>
  );
}
